import React, { useEffect, useMemo, useState } from "react";
import { JURIS as JURIS_RAW } from "./constants";
import { formatMoneyCompact } from "./helpers";

/**
 * Universal Audit — Inputs only
 * - Jurisdictions FIRST (no defaults)
 * - Business Context includes Industry + AI Use Case (niche removed)
 * - Sliders full-width with improved low-end granularity
 * - Currency pills only appear after jurisdictions selected
 * - Disclaimer gates Calculate
 *
 * Revenue slider mapping (Option A):
 * - Exponential curve: value = max * t^gamma
 * - Higher gamma => more precision at low values
 * - This fixes "tiny movement => huge number" behavior for REV_MAX=5B.
 */

function safeArray(x) {
  return Array.isArray(x) ? x : [];
}

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function clamp(n, min, max) {
  const v = Number(n);
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, v));
}

function currencyLabel(headlineCurrency) {
  if (!headlineCurrency) return null;
  if (headlineCurrency.code === "MIXED") return { code: "MIXED", symbol: "" };
  return { code: headlineCurrency.code || "USD", symbol: headlineCurrency.symbol || "$" };
}

/**
 * PEOPLE mapping (kept as-is; works well for 0..500k)
 * Piecewise:
 * - First 30% slider -> first 5% value (micro precision)
 * - Remaining 70% slider -> remaining 95% value (gentle growth)
 */
function peopleCurveValue(t, max) {
  const tt = clamp(t, 0, 1);

  const kneeT = 0.3;
  const kneeV = 0.05;

  if (tt <= kneeT) {
    const u = tt / kneeT; // 0..1
    return Math.round(u * (max * kneeV));
  }

  const u = (tt - kneeT) / (1 - kneeT); // 0..1
  const gamma = 1.6;
  const curved = Math.pow(u, gamma);
  return Math.round(max * (kneeV + (1 - kneeV) * curved));
}

function peopleCurveT(value, max) {
  const v = clamp(value, 0, max);

  const kneeT = 0.3;
  const kneeV = 0.05;

  const kneeValue = max * kneeV;

  if (v <= kneeValue) {
    const u = kneeValue > 0 ? v / kneeValue : 0; // 0..1
    return clamp(u * kneeT, 0, 1);
  }

  const u = (v - kneeValue) / (max - kneeValue); // 0..1
  const gamma = 1.6;
  const inv = Math.pow(clamp(u, 0, 1), 1 / gamma);
  return clamp(kneeT + (1 - kneeT) * inv, 0, 1);
}

/**
 * REVENUE mapping (Option A exponential curve)
 * - t in [0..1] -> value in [0..max]
 * - gamma tuned for REV_MAX=5B so early slider movement is not explosive.
 */
function revenueCurveValue(t, max, gamma = 4.6) {
  const tt = clamp(t, 0, 1);
  if (max <= 0) return 0;
  const curved = Math.pow(tt, gamma);
  return Math.round(curved * max);
}

function revenueCurveT(value, max, gamma = 4.6) {
  const v = clamp(value, 0, max);
  if (max <= 0) return 0;
  const ratio = v / max;
  return clamp(Math.pow(clamp(ratio, 0, 1), 1 / gamma), 0, 1);
}

export default function LeftPanel({
  // Business context
  industry,
  setIndustry,
  aiUseCase,
  setAiUseCase,

  // People
  PEOPLE_MAX,
  peoplePerMonth,
  peopleText,
  onPeopleTextChange,
  setPeopleFromSlider,

  // Revenue
  REV_MAX,
  annualRevenue,
  annualRevenueText,
  annualRevenueCurrency,
  setAnnualRevenueCurrency,
  onRevenueTextChange,
  setRevenueFromSlider,

  // Jurisdictions
  selectedJurKeys,
  toggleJur,

  // Currency
  headlineCurrency,

  // Disclaimer
  disclaimerAccepted,
  setDisclaimerAccepted,
  calculateDisabledReason,

  // CTA
  onCalculate,
}) {
  const JURIS = safeArray(JURIS_RAW);

  const cur = currencyLabel(headlineCurrency);

  // Slider internal state (0..1000)
  const [peopleSlider, setPeopleSlider] = useState(0);
  const [revSlider, setRevSlider] = useState(0);

  // Sync slider positions when external numbers change (typed input or hydration)
  useEffect(() => {
    const t = peopleCurveT(Number(peoplePerMonth) || 0, Number(PEOPLE_MAX) || 0);
    setPeopleSlider(Math.round(t * 1000));
  }, [peoplePerMonth, PEOPLE_MAX]);

  useEffect(() => {
    const t = revenueCurveT(Number(annualRevenue) || 0, Number(REV_MAX) || 0);
    setRevSlider(Math.round(t * 1000));
  }, [annualRevenue, REV_MAX]);

  const jurButtons = useMemo(() => {
    return JURIS.map((j) => {
      const selected = selectedJurKeys?.has?.(j.key);
      return (
        <button
          key={j.key}
          type="button"
          onClick={() => toggleJur(j.key)}
          className={cn(
            "rounded-2xl border px-4 py-3 text-left transition-colors",
            selected
              ? "bg-blue-700 border-blue-700 text-white"
              : "bg-white border-blue-200 text-blue-950 hover:border-blue-400"
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-black uppercase tracking-widest">{j.label}</div>
            <div
              className={cn(
                "rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.2em]",
                selected ? "border-white/25 text-white/90" : "border-slate-200 text-slate-500"
              )}
            >
              {j.key === "US_FED" ? "federal" : "privacy"}
            </div>
          </div>
          <div className={cn("mt-1 text-[11px]", selected ? "text-white/75" : "text-slate-500")}>
            {j.key === "UK"
              ? "UK data protection penalty references"
              : j.key === "EU"
              ? "EU AI Act regulatory references"
              : "Federal penalties apply only to qualifying conduct"}
          </div>
        </button>
      );
    });
  }, [JURIS, selectedJurKeys, toggleJur]);

  const canCalculate = !calculateDisabledReason && !!disclaimerAccepted;

  return (
    <div className="space-y-5">
      {/* Jurisdictions FIRST */}
      <div className="rounded-3xl border border-blue-200 bg-white p-5 shadow-lg shadow-blue-950/5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
              Jurisdictions
            </div>
            <div className="mt-2 text-sm text-slate-700">
              Select all that apply. If unsure, choose your primary operating region.
            </div>
            <div className="mt-2 text-xs text-slate-500">
              This determines the regimes included in your Results reveal.
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Currency pill only after selection */}
            {cur ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs">
                <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                  Currency
                </div>
                <div className="mt-1 font-black text-slate-800">{cur.code}</div>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                Selected
              </div>
              <div className="mt-1 font-black text-slate-800">{selectedJurKeys?.size || 0}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">{jurButtons}</div>
        <p className="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-950">This free preview covers U.S. federal regulations only; U.S. State laws are not included. State-specific regulatory analysis is available in the paid AFP Assessment.</p>

        {!selectedJurKeys?.size ? (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <span className="font-black">Next step:</span> Select at least one jurisdiction to continue.
          </div>
        ) : null}


      </div>

      {/* Business Context (Industry + AI Use Case in same container) */}
      <div className="rounded-3xl border border-blue-200 bg-white p-5 shadow-lg shadow-blue-950/5">
        <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
          Business Context
        </div>
        <div className="mt-2 text-sm text-slate-700">
          Keep it high-level. This labels your Results and your remediation roadmap.
        </div>

        <div className="mt-4">
          <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
            Industry
          </div>
          <input
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-slate-400"
            placeholder="e.g., Healthcare, Finance, Retail"
          />
        </div>

        <div className="mt-4">
          <div className="text-[11px] font-black uppercase tracking-[0.25em] text-slate-500">
            AI Use Case (within this industry)
          </div>
          <div className="mt-2 text-xs text-slate-500">
            1–3 sentences. What does the AI do and who does it affect?
          </div>
          <textarea
            value={aiUseCase}
            onChange={(e) => setAiUseCase(e.target.value)}
            className="mt-3 h-28 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-slate-400"
            placeholder="Example: We use AI to triage customer support tickets and suggest responses that agents approve before sending..."
          />
        </div>
      </div>

      {/* Sliders (full-width) */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* People */}
        <div className="rounded-3xl border border-blue-200 bg-white p-5 shadow-lg shadow-blue-950/5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                People Impacted / Month
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                Current
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <input
                  value={peopleText}
                  onChange={onPeopleTextChange}
                  inputMode="numeric"
                  className="w-28 rounded-xl border border-slate-200 bg-white px-3 py-2 text-right text-sm font-black text-slate-900 outline-none focus:border-slate-400"
                />
                <div className="text-xs text-slate-500">/mo</div>
              </div>
            </div>
          </div>

          <input
            type="range"
            min={0}
            max={1000}
            value={peopleSlider}
            onChange={(e) => {
              const s = clamp(e.target.value, 0, 1000);
              setPeopleSlider(s);
              const t = s / 1000;
              const v = peopleCurveValue(t, PEOPLE_MAX);
              setPeopleFromSlider(v);
            }}
            className="mt-4 w-full"
          />

          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <div>0</div>
            <div>
              Max: <span className="font-black text-slate-800">{PEOPLE_MAX.toLocaleString("en-US")}</span>
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="rounded-3xl border border-blue-200 bg-white p-5 shadow-lg shadow-blue-950/5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.35em] text-slate-500">
                Annual Revenue Estimate
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                Current
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <input
                  value={annualRevenueText}
                  onChange={onRevenueTextChange}
                  inputMode="numeric"
                  className="w-44 rounded-xl border border-slate-200 bg-white px-3 py-2 text-right text-sm font-black text-slate-900 outline-none focus:border-slate-400"
                />
                <select aria-label="Annual revenue currency" value={annualRevenueCurrency || ""} onChange={(e) => setAnnualRevenueCurrency(e.target.value)} className="rounded-lg border border-blue-300 bg-white px-2 py-2 text-xs font-bold text-blue-950">
                  <option value="">Currency</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
          </div>

          <p className="mt-2 text-xs text-slate-600">For regulatory turnover examples, we illustrate what would happen <strong>if</strong> this amount were your prior-year total worldwide turnover. We do not verify that assumption.</p>
          <input
            type="range"
            min={0}
            max={1000}
            value={revSlider}
            onChange={(e) => {
              const s = clamp(e.target.value, 0, 1000);
              setRevSlider(s);
              const t = s / 1000;
              const v = revenueCurveValue(t, REV_MAX);
              setRevenueFromSlider(v);
            }}
            className="mt-4 w-full"
          />

          <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
            <div>0</div>
            <div>
              Max:{" "}
              <span className="font-black text-slate-800">
                {formatMoneyCompact(REV_MAX, ({USD:"$",EUR:"€",GBP:"£"})[annualRevenueCurrency] || "$")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Acknowledgment (wide + short) */}
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-black uppercase tracking-[0.35em] text-amber-900">
              Acknowledgment Required
            </div>
            <div className="mt-2 text-sm text-amber-950">
              This free Universal Risk Audit provides <span className="font-black">illustrative regulatory penalty references</span> and is{" "}
              <span className="font-black">not legal advice</span>.
            </div>
          </div>

          <div className="rounded-2xl border border-amber-200 bg-white px-4 py-3 text-xs text-amber-900">
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-800">Required</div>
            <div className="mt-1 font-black">{disclaimerAccepted ? "Accepted" : "Not yet"}</div>
          </div>
        </div>

        <label className="mt-3 flex cursor-pointer select-none items-start gap-3">
          <input
            type="checkbox"
            checked={!!disclaimerAccepted}
            onChange={(e) => setDisclaimerAccepted(!!e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300"
          />
          <div className="text-sm text-amber-950">
            I understand this free preview illustrates selected regulatory penalty provisions, is not a determination of my company's compliance or an estimate of a fine I will receive, and is not legal advice.
          </div>
        </label>

        {calculateDisabledReason ? (
          <div className="mt-3 rounded-2xl border border-amber-200 bg-white p-4 text-xs text-amber-900">
            <div className="font-black">To continue:</div>
            <div className="mt-1">{calculateDisabledReason}</div>
          </div>
        ) : null}
      </div>

      {/* Calculate */}
      <button
        type="button"
        onClick={onCalculate}
        disabled={!canCalculate}
        className={cn(
          "w-full rounded-3xl px-6 py-4 text-sm font-black uppercase tracking-widest text-white transition-colors shadow-sm",
          canCalculate ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-300 cursor-not-allowed"
        )}
        title={!canCalculate ? "Complete the required steps to continue." : "Explore Potential Exposure"}
      >
        {canCalculate ? "Explore Potential Exposure" : "Complete Audit to View Preview"}
      </button>

      <div className="h-2" />
    </div>
  );
}
